
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarEvent } from '@/types/calendar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { EditEventFormFields } from './EditEventFormFields';
import { EventSchema } from '../schemas/eventSchema';
import { useUpdateEvent } from '@/hooks/useEvents';
import { toast } from 'sonner';

interface EditEventDialogProps {
  event: CalendarEvent;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditEventDialog: React.FC<EditEventDialogProps> = ({
  event,
  isOpen,
  onClose,
  onSuccess
}) => {
  const updateEvent = useUpdateEvent();

  const form = useForm({
    resolver: zodResolver(EventSchema),
    defaultValues: {
      title: event.title || '',
      description: event.description || '',
      date: new Date(event.start_time),
      startTime: new Date(event.start_time).toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      endTime: new Date(event.end_time).toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      location: event.location || '',
      type: event.type || 'other',
      attendees: event.attendees ? event.attendees.join(', ') : '',
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const startDateTime = new Date(data.date);
      const endDateTime = new Date(data.date);
      
      const [startHour, startMinute] = data.startTime.split(':');
      const [endHour, endMinute] = data.endTime.split(':');
      
      startDateTime.setHours(parseInt(startHour), parseInt(startMinute));
      endDateTime.setHours(parseInt(endHour), parseInt(endMinute));

      const updatedEvent = {
        ...event,
        title: data.title,
        description: data.description,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        location: data.location,
        type: data.type,
        attendees: data.attendees ? data.attendees.split(',').map((email: string) => email.trim()).filter(Boolean) : [],
        updated_at: new Date().toISOString(),
      };

      await updateEvent.mutateAsync(updatedEvent);
      toast.success('Event updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event. Please try again.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <EditEventFormFields form={form} />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={updateEvent.isPending}
              >
                {updateEvent.isPending ? "Updating..." : "Update Event"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
